import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { certificateApi } from "configs/api";

export interface Certificate {
  id?: string;
  _id?: string;
  userID: string;
  courseID: string;
  enrollmentID: string;
  certificateNumber: string;
  issuedAt: string;
  completedAt?: string;
  completionProgress?: number;
  enrollmentStatus?: string;
  courseName?: string;
  instructorName?: string;
  pdfUrl?: string;
  imageUrl?: string;
  verified?: boolean;
}

interface CertificateState {
  certificates: Certificate[];
  currentCertificate: Certificate | null;
  loading: boolean;
  error: string | null;
}

const initialState: CertificateState = {
  certificates: [],
  currentCertificate: null,
  loading: false,
  error: null,
};

// Async thunks
export const fetchUserCertificates = createAsyncThunk(
  "certificate/fetchUser",
  async (userId?: string) => {
    const url = userId
      ? `/api/certificates/user/${userId}`
      : `/api/certificates/user`;
    const response = await certificateApi.get(url);
    return response.data?.data || response.data || [];
  }
);

export const fetchCertificate = createAsyncThunk(
  "certificate/fetchOne",
  async (id: string) => {
    const response = await certificateApi.get(`/api/certificates/${id}`);
    return response.data?.data || response.data;
  }
);

export const generateCertificate = createAsyncThunk(
  "certificate/generate",
  async (data: {
    userID: string;
    courseID: string;
    enrollmentID: string;
    certificateNumber: string;
    issuedAt?: string;
    pdfUrl?: string;
    imageUrl?: string;
  }) => {
    const payload = {
      ...data,
      issuedAt: data.issuedAt || new Date().toISOString(),
    };
    const response = await certificateApi.post("/api/certificates", payload);
    return response.data?.data || response.data;
  }
);

export const verifyCertificate = createAsyncThunk(
  "certificate/verify",
  async (certificateId: string) => {
    const response = await certificateApi.get(`/api/certificates/${certificateId}`);
    return response.data?.data || response.data;
  }
);

const certificateSlice = createSlice({
  name: "certificate",
  initialState,
  reducers: {
    setCurrentCertificate: (state, action: PayloadAction<Certificate | null>) => {
      state.currentCertificate = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserCertificates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserCertificates.fulfilled, (state, action) => {
        state.loading = false;
        state.certificates = action.payload;
      })
      .addCase(fetchUserCertificates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch certificates";
      })
      .addCase(fetchCertificate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCertificate.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCertificate = action.payload;
      })
      .addCase(fetchCertificate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch certificate";
      })
      .addCase(generateCertificate.fulfilled, (state, action) => {
        state.certificates.push(action.payload);
      })
      .addCase(verifyCertificate.fulfilled, (state, action) => {
        state.currentCertificate = action.payload;
      });
  },
});

export const { setCurrentCertificate, clearError } = certificateSlice.actions;
export default certificateSlice.reducer;

